<script setup>
/**
 * OrdersReportsModal
 * Presentational modal window displaying detailed financial product aggregates.
 * Migrated to Composition API to eliminate pipeline filter bugs and provide
 * reactive options tracking to ensure smooth cross-month report switching.
 */
import {ref, computed, onMounted} from 'vue'
import api from '@/services/api'

const props = defineProps({
    options: {
        type: Object,
        required: true
    },
})

const emit = defineEmits(['hide-modal'])

// --- Reactive State Layer ---

const productData = ref(undefined)

// --- Reactive Computed Properties (FIX: Resolves Static Option Gaps) ---

const month = computed(() => props.options?.month)
const year = computed(() => props.options?.year)
const monthName = computed(() => props.options?.monthName || '')

// --- Safe Functional Replacement for Filters ---

/**
 * Standardizes decimal places across financial output rows cleanly using Intl specifications.
 * FIX: Forces a minimum and maximum trailing scale of 2 decimal places.
 */
const formatCurrency = (value) => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return '0.00'

    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericValue)
}

// --- Methods ---

/**
 * Dispatches a backend request to fetch historical product data.
 */
const fetchProductReportData = async () => {
    try {
        const response = await api.post('api/OrdersController/getMonthsProducts', {
            year: year.value,
            month: month.value
        })
        productData.value = response?.data?.productData || []
    } catch (err) {
        console.error('Failed to load monthly order report metrics:', err)
        productData.value = []
    }
}

/**
 * Safely navigates the browser shell to target orders without disrupting SPA state.
 */
const handleNavigationToOrder = (orderId) => {
    if (!orderId) return
    // Modifying hash protects local runtime environments better than direct window assignments
    window.location.hash = `/m_basket_orders/${orderId}`
    emit('hide-modal')
}

// --- Lifecycle ---

onMounted(() => {
    fetchProductReportData()
})
</script>

<template>
    <div class="modal-dialog modal-xl">
        <div class="modal-content border-0 shadow-lg">

            <div class="modal-header">
                <h4 class="modal-title fw-bold">
                    Products for {{ monthName }} {{ year }}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$emit('hide-modal')"
                />
            </div>

            <div class="modal-body p-0 pb-3">

                <div v-if="productData" class="table-responsive">
                    <table class="table table-hover align-middle mb-0 text-nowrap">
                        <thead>
                        <tr>
                            <th class="ps-3">Title</th>
                            <th class="text-end">Quantity</th>
                            <th class="text-end">Net</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Gross</th>
                            <th class="text-end">Shipping</th>
                            <th class="text-end">Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="productData.length === 0">
                            <td colspan="7" class="text-center py-4 text-muted small">
                                No transaction logs available for this period.
                            </td>
                        </tr>

                        <tr v-for="(data, k) in productData" :key="k">
                            <td class="ps-3 fw-medium">
                                <a
                                    href="#"
                                    class="text-decoration-none text-primary"
                                    @click.prevent="handleNavigationToOrder(data.orderId)"
                                >
                                    {{ data.title }}
                                </a>
                            </td>
                            <td class="text-end font-monospace small">
                                {{ data.quantity }}
                            </td>
                            <td class="text-end font-monospace small">
                                &pound;{{ formatCurrency(data.net) }}
                            </td>
                            <td class="text-end font-monospace small">
                                &pound;{{ formatCurrency(data.tax) }}
                            </td>
                            <td class="text-end font-monospace small">
                                &pound;{{ formatCurrency(data.gross) }}
                            </td>
                            <td class="text-end font-monospace small">
                                &pound;{{ formatCurrency(data.shipping) }}
                            </td>
                            <td class="text-end font-monospace small fw-bold pe-3">
                                &pound;{{ formatCurrency(Number(data.gross) + Number(data.shipping)) }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div v-else class="p-5 text-center my-3">
                    <div class="spinner-border text-secondary" role="status">
                        <span class="visually-hidden">Loading transactional data...</span>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<style scoped>
/* Numerical reporting grids use explicit space metrics to keep labels neatly aligned */
.text-nowrap {
    white-space: nowrap;
}

.font-monospace {
    font-family: monospace;
}
</style>